/**
 * Reset Password Component
 *
 * Handles the password reset flow for Supabase Auth.
 * Supabase redirects to /reset-password#access_token=... when the user
 * clicks the email link. The Supabase client automatically parses this URL
 * hash and fires an onAuthStateChange event with type PASSWORD_RECOVERY,
 * which sets the user in AuthContext. No manual URL parsing is needed here.
 */

import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Alert,
  Paper,
  CircularProgress
} from '@mui/material';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { updatePassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      return setError('Passwords do not match');
    }

    if (newPassword.length < 8) {
      return setError('Password must be at least 8 characters long');
    }

    if (!/\d/.test(newPassword) || !/[a-zA-Z]/.test(newPassword)) {
      return setError('Password must contain both letters and numbers');
    }

    setLoading(true);

    try {
      await updatePassword(newPassword);
      setSuccess(true);
      setTimeout(() => navigate('/account'), 2000);
    } catch (err) {
      setError(err.message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ color: '#8B4513' }}>
          Set New Password
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Password updated successfully! Redirecting to your account...
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Typography variant="body1" gutterBottom>
              Enter your new password below.
            </Typography>

            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type="password"
              id="newPassword"
              autoComplete="new-password"
              autoFocus
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="At least 8 characters with letters and numbers"
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                bgcolor: '#8B4513',
                '&:hover': { bgcolor: '#6d3610' }
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
            </Button>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <Link to="/login" style={{ textDecoration: 'none', color: '#8B4513' }}>
            <Typography variant="body2">
              Back to Login
            </Typography>
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default ResetPassword;
